// Lib
import React from 'react';
import PropTypes from 'prop-types';

// Utils
import { prop } from '../../../../common/utils/immutableHelper';
import { getElementCardCropInset, getElementCardRenderedWidthPx } from '../../utils/elementUtil';
import { getData, getDataFilename, getDataType, getFetching } from '../../attachments/attachmentsSelector';
import {
    getElementId,
    getFileProp,
    getImageProp,
    getMediaAudioOnly,
} from '../../../../common/elements/utils/elementPropertyUtils';
import { getMainEditorKey } from '../../utils/elementEditorUtils';
import fileCanShowMedia from '../utils/fileCanShowMedia';

// Components
import EditableTitle from '../../../components/editableTitle/EditableTitle';
import DetailView from '../../../components/displayMode/DetailView';
import { FilePreviewWithImageHover } from './FilePreview';
import FileIcon from '../../../components/fileIcon/FileIcon';
import FileSecondaryDetails from './FileSecondaryDetails';
import FileUploadProgress from './FileUploadProgress';

// Constants
import { FILE_TYPES_INFO } from '../../../../common/files/fileConstants';

// Styles
import './FileDetailViewContent.scss';

const FileDetailViewContent = (props) => {
    const {
        element,
        isEditable,
        isSingleSelected,
        isResizingOrAnimating,
        filterQuery,
        attachment,
        showDownloadOption,
        shouldDisplayMediaOnly,
        inList,
    } = props;
    const fileData = getFileProp(element) || getData(attachment);
    const imageData = getImageProp(element);
    const widthPx = getElementCardRenderedWidthPx(props);
    const savedWidthPx = getElementCardRenderedWidthPx({ ...props, tempSize: null });

    const isAttachmentAnImagePreview = getDataType(attachment) === FILE_TYPES_INFO.IMAGE.type;
    const awaitingPreview = !imageData && fileCanShowMedia(element, fileData);
    const isFetchingData = getFetching(attachment) && getDataFilename(attachment);

    const showProgress = !isAttachmentAnImagePreview && (awaitingPreview || isFetchingData);
    const showMetadata = !shouldDisplayMediaOnly;

    const fallbackTitle = prop('filename', fileData);
    const audioOnly = getMediaAudioOnly(element);

    const editorKey = getMainEditorKey(props);

    const cropInset = getElementCardCropInset(inList);

    return (
        <DetailView
            className="FileDetailViewContent"
            previewElement={
                <FilePreviewWithImageHover
                    {...props}
                    cropToGrid={!isResizingOrAnimating}
                    cropInset={cropInset}
                    widthPx={widthPx}
                    savedWidthPx={savedWidthPx}
                    audioOnly={audioOnly}
                />
            }
            iconElement={showMetadata && <FileIcon fileType={prop('type', fileData)} audioOnly={audioOnly} />}
            titleElement={
                showMetadata && (
                    <EditableTitle
                        element={element}
                        elementId={getElementId(element)}
                        isEditable={isEditable}
                        initialValue={fallbackTitle}
                        maxLength={500}
                        selectFirst
                        selectAllOnEdit
                        isSelected={isSingleSelected}
                        filterQuery={filterQuery}
                        editorKey={editorKey}
                    />
                )
            }
            secondaryElement={
                showMetadata &&
                (showProgress ? (
                    <FileUploadProgress element={element} attachment={attachment} />
                ) : (
                    <FileSecondaryDetails
                        element={element}
                        fileData={fileData}
                        showDownloadOption={showDownloadOption}
                    />
                ))
            }
        />
    );
};

FileDetailViewContent.propTypes = {
    element: PropTypes.object.isRequired,
    attachment: PropTypes.object,
    isEditable: PropTypes.bool,
    isSingleSelected: PropTypes.bool,
    isResizing: PropTypes.bool,
    filterQuery: PropTypes.string,
    showDownloadOption: PropTypes.bool,
    isResizingOrAnimating: PropTypes.bool,
    shouldDisplayMediaOnly: PropTypes.bool,
    inList: PropTypes.string,
};

export default FileDetailViewContent;
